/**
 * API Keys management section
 */

import { Key01, Trash01 } from '@untitledui/icons';

import { useCanWrite } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { AlertFloating } from '@/lib/base/overlays/alerts';
import type { APIKey } from '@/lib/profile/types';

interface APIKeysSectionProps {
  apiKeys: APIKey[];
  newApiKey: string | null;
  showNewKeyModal: boolean;
  creating: boolean;
  onCreateKey: () => void;
  onRevokeKey: (keyId: number) => void;
  onCloseNewKeyModal: () => void;
}

export function APIKeysSection({
  apiKeys,
  newApiKey,
  showNewKeyModal,
  creating,
  onCreateKey,
  onRevokeKey,
  onCloseNewKeyModal,
}: APIKeysSectionProps) {
  const canWrite = useCanWrite();

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <h3 className='text-base font-semibold text-primary flex items-center gap-2'>
          <Key01 className='w-5 h-5' />
          API Keys
        </h3>
        {canWrite && (
          <Button
            onClick={onCreateKey}
            color='primary'
            size='sm'
            isLoading={creating}
          >
            Create API Key
          </Button>
        )}
      </div>

      {/* New API Key Modal */}
      {showNewKeyModal && newApiKey && (
        <AlertFloating
          color='success'
          title='API Key Created'
          description={
            <>
              <span className='text-sm block mb-2'>
                Save this key now - you won't be able to see it again!
              </span>
              <span className='bg-bg-secondary p-3 rounded-lg block'>
                <code className='text-sm font-mono break-all'>{newApiKey}</code>
              </span>
            </>
          }
          onClose={onCloseNewKeyModal}
          dismissLabel='Close'
        />
      )}

      {/* API Keys List */}
      {apiKeys.length === 0 ? (
        <p className='text-tertiary text-sm'>No API keys created yet.</p>
      ) : (
        <div className='space-y-3'>
          {apiKeys.map(key => (
            <div
              key={key.id}
              className='flex items-center justify-between p-4 border border-border-secondary rounded-lg'
            >
              <div className='flex-1'>
                <p className='font-mono text-sm text-primary'>{key.prefix}…</p>
                <p className='text-xs text-tertiary mt-1'>
                  Created {new Date(key.created).toLocaleDateString()}
                </p>
              </div>
              {canWrite && (
                <Button
                  onClick={() => onRevokeKey(key.id)}
                  color='secondary-destructive'
                  size='sm'
                  iconLeading={Trash01}
                  aria-label='Revoke API key'
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
